import { z } from 'zod';

/**
 * `pending-design-aware-visual-checks` v0 — wire types for pinning a
 * Figma frame to a route + viewport so the existing visual-baseline
 * loop has a design source-of-truth, not yesterday's screenshot.
 *
 * Locked decisions (this sweep):
 *   - Manual pin in v0 (paste a Figma share URL + the resolvable PNG export).
 *   - One pin per (project, route_glob, viewport).
 *   - SSIM threshold per pin; default 0.95.
 */

export const DESIGN_PIN_VIEWPORTS = ['desktop', 'tablet', 'mobile'] as const;
export type DesignPinViewport = (typeof DESIGN_PIN_VIEWPORTS)[number];

/**
 * `pending-design-aware-visual-checks` v0.2 — multi-provider support.
 * v0 / v0.1 shipped Figma only. v0.2 adds Sketch + Penpot under the
 * same pin / resolver / SSIM loop.  The closed enum lives here so
 * contract changes ship a migration, not a free-text drift.
 */
export const DESIGN_PROVIDERS = ['figma', 'sketch', 'penpot'] as const;
export type DesignProvider = (typeof DESIGN_PROVIDERS)[number];

export const DesignPinCreateRequestSchema = z.object({
  projectId: z.string().uuid(),
  routeGlob: z.string().min(1).max(500),
  viewport: z.enum(DESIGN_PIN_VIEWPORTS).default('desktop'),
  figmaFrameUrl: z.string().url(),
  /**
   * Publicly resolvable PNG URL of the Figma frame. v0 the user
   * pastes this (e.g. `?node-id=...&format=png` export URL or a
   * pre-signed S3 mirror). v0.1 adds an OAuth-backed resolver that
   * accepts just the share URL.
   */
  figmaFramePngUrl: z.string().url(),
  ssimThreshold: z.number().gt(0).lte(1).default(0.95),
});
export type DesignPinCreateRequest = z.infer<typeof DesignPinCreateRequestSchema>;

export const DesignPinSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  projectId: z.string().uuid(),
  /**
   * Source design tool. v0.2 expands the v0 Figma-only union to
   * `'figma' | 'sketch' | 'penpot'`. Existing rows back-fill to
   * `'figma'` per Migration 138.
   */
  provider: z.enum(DESIGN_PROVIDERS).default('figma'),
  routeGlob: z.string(),
  viewport: z.enum(DESIGN_PIN_VIEWPORTS),
  /**
   * Source URL the operator pasted (kept under the `figma*` name
   * for back-compat — column rename is destructive and the v0 row
   * shape is already on the wire).
   */
  figmaFrameUrl: z.string().url(),
  figmaFramePngUrl: z.string().url(),
  ssimThreshold: z.number(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type DesignPin = z.infer<typeof DesignPinSchema>;

export const DesignDiffResultSchema = z.object({
  pinId: z.string().uuid(),
  routeGlob: z.string(),
  viewport: z.enum(DESIGN_PIN_VIEWPORTS),
  /** Mean SSIM in [0,1]; 1 = identical, threshold default 0.95. */
  ssim: z.number(),
  /** Whether the rendered screenshot beat the per-pin threshold. */
  passed: z.boolean(),
  /**
   * Best-effort note pointing the operator at the specific pin
   * miss (e.g. "ssim 0.83 < threshold 0.95"). Localized strings
   * stay in `@rynora/messages` per AGENTS.md rule 4.
   */
  reasonCode: z.enum(['below_threshold', 'image_load_failed', 'pin_missing']).nullable(),
});
export type DesignDiffResult = z.infer<typeof DesignDiffResultSchema>;

/**
 * `pending-design-aware-visual-checks` v0.1 — single-URL pin-create
 * request. The pre-v0.1 `DesignPinCreateRequestSchema` required BOTH
 * `figmaFrameUrl` AND `figmaFramePngUrl` because v0 had no PNG
 * resolver. v0.1 ships the Figma PAT resolver service that derives
 * the PNG URL server-side, so the SPA paste-flow now sends ONLY
 * `figmaFrameUrl`.
 *
 * The v0 shape is still accepted (back-compat for any CLI / SDK
 * consumer that wrote against the v0 contract before v0.1 landed) —
 * the resolver service only kicks in when `figmaFramePngUrl` is
 * absent.
 *
 * Locked decisions:
 *   - `figmaFrameUrl` must pass `validateFigmaFrameUrl()` (handled at
 *     the service layer; the schema only checks URL shape so the
 *     diagnostic code falls out of the parser, not Zod).
 *   - Optional `ssimThreshold` defaults to `designAware.similarityThreshold`
 *     (admin-config), which itself defaults to 0.95 (locked §5).
 */
export const DesignPinCreateV01RequestSchema = z.object({
  projectId: z.string().uuid(),
  routeGlob: z.string().min(1).max(500),
  viewport: z.enum(DESIGN_PIN_VIEWPORTS).default('desktop'),
  figmaFrameUrl: z.string().url(),
  /**
   * Optional — v0 shape. When omitted, the v0.1 PAT resolver derives
   * it from `figmaFrameUrl`. When present, the resolver step is
   * skipped (caller has already resolved the PNG).
   */
  figmaFramePngUrl: z.string().url().optional(),
  ssimThreshold: z.number().gt(0).lte(1).optional(),
});
export type DesignPinCreateV01Request = z.infer<typeof DesignPinCreateV01RequestSchema>;

/**
 * Resolver result. Returned from `resolveFigmaPng()` and embedded in
 * the v0.1 pin-create response so the SPA can display "resolved
 * PNG from Figma · cached <n>s ago" without a second round-trip.
 */
export const FigmaResolveResultSchema = z.object({
  fileKey: z.string(),
  nodeId: z.string(),
  /** Publicly resolvable PNG URL (the value stored on the `design_pins` row). */
  resolvedUrl: z.string().url(),
  /** `true` when this resolve was served from `design_frame_cache`. */
  fromCache: z.boolean(),
  /** ISO-8601 of the cache row's `fetched_at` (the resolve time). */
  fetchedAt: z.string().datetime(),
});
export type FigmaResolveResult = z.infer<typeof FigmaResolveResultSchema>;

/**
 * Provider-agnostic resolve result emitted by the v0.2 dispatcher.
 * `fileKey` / `nodeId` are kept Figma-named for back-compat with the
 * v0.1 cache table; for Sketch they store `(shareId, artboardId)`
 * and for Penpot `(fileId, boardId)`.
 */
export const DesignResolveResultSchema = z.object({
  provider: z.enum(DESIGN_PROVIDERS),
  fileKey: z.string(),
  nodeId: z.string(),
  resolvedUrl: z.string().url(),
  fromCache: z.boolean(),
  fetchedAt: z.string().datetime(),
});
export type DesignResolveResult = z.infer<typeof DesignResolveResultSchema>;

/**
 * Listing response — what `GET /api/design-pins?projectId=…` returns.
 */
export const ListDesignPinsResponseSchema = z.object({
  pins: z.array(DesignPinSchema),
});
export type ListDesignPinsResponse = z.infer<typeof ListDesignPinsResponseSchema>;

/**
 * Public design-aware config the SPA reads from `GET /api/config/public`.
 * Mirrors the operator-tunable knobs on `admin_config.designAware`
 * minus the encrypted PATs (replaced by `hasXxxToken: boolean` flags).
 */
export const PublicDesignAwareConfigSchema = z.object({
  enabled: z.boolean(),
  /** `true` ⇒ a Figma PAT has been pasted into admin-config (encrypted at rest). */
  hasFigmaToken: z.boolean(),
  /** `true` ⇒ a Sketch PAT has been pasted (v0.2). */
  hasSketchToken: z.boolean(),
  /** `true` ⇒ a Penpot PAT has been pasted (v0.2). */
  hasPenpotToken: z.boolean(),
  /**
   * Penpot REST base URL (default `https://design.penpot.app`).
   * Surfaced to the SPA so self-hosted operators can see which
   * Penpot instance the integration is talking to.
   */
  penpotBaseUrl: z.string().url(),
  /** Default per-pin SSIM threshold the SPA pre-fills (locked §5: 0.95). */
  similarityThreshold: z.number(),
  /** Cache TTL the SPA shows in the "cached <n>s ago" copy. */
  cacheSeconds: z.number().int().nonnegative(),
});
export type PublicDesignAwareConfig = z.infer<typeof PublicDesignAwareConfigSchema>;
